/**
 * THE issue-tracker status vocabulary of the admin — tones and fallback labels
 * for the coarse status buckets clockout-service serves alongside every
 * mirrored issue.
 *
 * Deliberately provider-agnostic. The service already abstracts the integration
 * behind `services/issue-tracker/` with a provider factory, so if a second
 * tracker (Notion, Linear) is ever mirrored it reuses these components rather
 * than forking a Jira-shaped copy.
 *
 * LABELS LIVE ON THE SERVER. `GET /api/admin/jira/status-map` returns
 * `{ key, label, order }` per status, and clockout-service marks that prose
 * PROVISIONAL pending product sign-off — the KEYS are the contract, the words
 * are not. The labels below are only a fallback for the moment before the map
 * arrives (or when it fails to load); a caller that has the map must pass its
 * label through.
 *
 * `tone` values must match the suffix of the SCSS class `tone_<tone>` in
 * `common/styles/status-pill-tag.module.scss`.
 *
 * Colour semantics follow the admin's existing convention (see
 * `reports/elements/syncStatus.constants.js`): blue = in motion, green = done,
 * grey = no information / does not apply. `queued` uses the softer blue tint so
 * it reads as "logged, not started" rather than as progress.
 */
export const ISSUE_STATUS_UNMAPPED = 'unmapped';

/**
 * The buckets a mirrored issue can actually BE in — the three clockout-service
 * publishes (`Constants.JIRA.STATUSES.LIST`), in its order.
 *
 * There is no `closed` and no `unmapped` here, and that is the contract, not an
 * omission: a board status with no FI-facing word (In Product, Deprecated, or
 * one nobody has mapped yet) is not mirrored at all, so nothing can arrive
 * labelled with either. `unmapped` survives below as a RENDER fallback only —
 * see `getIssueStatus`.
 */
export const ISSUE_STATUS_BUCKETS = Object.freeze(['queued', 'in_progress', 'done']);

export const ISSUE_STATUSES = Object.freeze({
    queued: {
        label: 'Queued',
        tone: 'soft',
        description: 'Logged and prioritised, but work has not started yet.',
    },
    in_progress: {
        label: 'In progress',
        tone: 'info',
        description: 'Actively being worked on by the Clockout team.',
    },
    done: {
        label: 'Done',
        tone: 'success',
        description: 'Completed and delivered.',
    },
    // NOT A BUCKET, and deliberately not in ISSUE_STATUS_BUCKETS: nothing can
    // arrive in this status, because the mirror hides an issue that leaves the
    // three above rather than relabelling it. It is what `getIssueStatus` falls
    // back to for a value this build does not recognise — a vocabulary the
    // server grew and this bundle has not — so such an issue is still drawn,
    // neutral and carrying its raw value, instead of vanishing from the row.
    [ISSUE_STATUS_UNMAPPED]: {
        label: 'Unmapped',
        tone: 'neutral',
        description:
            'This issue sits in a tracker status this version of the admin does not know. ' +
            'It is shown rather than hidden so the work is never invisible.',
    },
});

/**
 * Resolve a status key to its display entry. A key this build does not
 * recognise is still VISIBLE — neutral, with the raw value in the description
 * so support can report it — and is never rendered as another status.
 *
 * @param {string | null | undefined} status
 * @param {{ label?: string }} [served] the entry from the served status map, when loaded
 * @returns {{ key: string, label: string, tone: string, description: string, recognised: boolean }}
 */
export function getIssueStatus(status, served) {
    if (status && Object.prototype.hasOwnProperty.call(ISSUE_STATUSES, status)) {
        const entry = ISSUE_STATUSES[status];
        return { key: status, recognised: true, ...entry, label: served?.label || entry.label };
    }

    const unmapped = ISSUE_STATUSES[ISSUE_STATUS_UNMAPPED];
    return {
        key: ISSUE_STATUS_UNMAPPED,
        recognised: status == null,
        tone: unmapped.tone,
        label: served?.label || unmapped.label,
        description:
            status == null
                ? 'Status not provided by the server.'
                : `Unrecognised status value "${status}" — this admin build does not know it.`,
    };
}

/**
 * Every status bucket as a board column, in the server's order.
 *
 * The board's fallback until `GET /api/admin/jira/status-map` lands, so the
 * board is never briefly column-less — and, more importantly, never briefly a
 * DIFFERENT board. Built from ISSUE_STATUS_BUCKETS and NOT from every entry in
 * ISSUE_STATUSES: `unmapped` is a render fallback, and a board that drew a
 * column for it would be offering a column nothing can ever be fetched into,
 * which then disappeared the moment the served map arrived.
 */
export const ISSUE_STATUS_COLUMNS = Object.freeze(
    ISSUE_STATUS_BUCKETS.map((key) => Object.freeze({ key, label: ISSUE_STATUSES[key].label })),
);
